/**
 * @fileoverview TransferModal - Modal component for transferring sessions between rooms
 * 
 * Provides a user interface for proctors to transfer student sessions from one room
 * to another. Handles room selection, optional notes, transfer execution, and success
 * feedback with auto-close functionality.
 * 
 * @module TransferModal
 */

import {
  Text,
  Heading,
  Modal,
  CloseButton,
  View,
  Spinner,
  Flex,
  Alert,
  List,
  Icon,
  IconRefreshLine,
} from '@smarterservices/smarter-ui';
import { Button, IconButton, SimpleSelect, TextArea } from '@instructure/ui';
import useSessionStore from '../../stores/session';
import { useEffect, useState } from 'react';
import { getAvailableRoomsForTransfer } from '../../lib/api';
import { Callout } from '../Common/Callout';
import useAppStore from '../../stores/app';

/**
 * TransferModal - Modal for transferring sessions between rooms
 * 
 * Displays a modal that allows proctors to:
 * - View available rooms for transfer
 * - Select a destination room
 * - Add optional transfer notes
 * - Execute the transfer
 * - View success confirmation with auto-close
 * 
 * Features:
 * - Auto-selects room if only one is available
 * - Validates room selection before allowing transfer
 * - Shows loading, success, and error states
 * - Auto-closes 5 seconds after successful transfer
 * - Provides retry functionality if no rooms are available
 * 
 * @component
 * @returns {React.ReactElement} Transfer modal component
 */
const TransferModal = () => {
  // Use selective subscriptions to prevent re-renders when session objects change
  const setTransferSessionSid = useSessionStore((state) => state.setTransferSessionSid);
  const transferSessionSid = useSessionStore((state) => state.transferSessionSid);
  const getSessionData = useSessionStore((state) => state.getSessionData);
  const transfer = useSessionStore((state) => state.transfer);
  const focusedSessionSid = useSessionStore((state) => state.focusedSessionSid);

  // State: Available rooms list
  const [rooms, setRooms] = useState([]);

  // State: Current session data
  const [session, setSession] = useState();

  // State: Set Current location of the session
  const [sessionLocation, setSessionLocation] = useState(null);

  // State: Room loading state ('loading', 'loaded', 'error')
  const [roomsLoadingState, setRoomsLoadingState] = useState('loading');

  // State: Selected room SID
  const [selectedRoom, setSelectedRoom] = useState('');

  // State: Optional transfer notes
  const [note, setNote] = useState('');

  // State: Transfer state ('idle', 'transferring', 'success', 'error')
  const [transferState, setTransferState] = useState('idle');

  /**
   * Loads available rooms for transfer
   * 
   * Fetches the list of rooms that can accept the current session transfer.
   * Updates roomsLoadingState based on API response.
   * 
   * @async
   * @function loadRooms
   * @returns {Promise<void>}
   */
  const loadRooms = async () => {
    if (!transferSessionSid) {
      console.warn('[TransferModal] No transfer session SID available');
      return;
    }

    setRoomsLoadingState('loading');

    try {
      const rooms = await getAvailableRoomsForTransfer(transferSessionSid);

      if (rooms.successful && rooms.data) {
        setRooms(rooms.data);
        setRoomsLoadingState('loaded');
      } else {
        console.error('[TransferModal] Failed to load rooms:', rooms);
        setRoomsLoadingState('error');
      }
    } catch (error) {
      console.error('[TransferModal] Error loading rooms:', error);
      setRoomsLoadingState('error');
    }
  };


  /**
   * Effect: Initialize modal data on mount
   * Loads session data and available rooms when modal opens
   */
  useEffect(() => {
    if (!transferSessionSid) return;

    const sessionData = getSessionData(transferSessionSid);
    if (!sessionData) {
      console.warn('[TransferModal] No session data found for SID:', transferSessionSid);
      return;
    }

    setSession(sessionData);
    setSessionLocation(sessionData.location);
    loadRooms();
  }, [transferSessionSid, getSessionData]);

  /**
   * Effect: Auto-select room if only one is available
   * Improves UX by pre-selecting when there's no choice to make
   */
  useEffect(() => {
    if (rooms.length === 1 && rooms[0]?.value) {
      setSelectedRoom(rooms[0].value);
    }
  }, [rooms]);

  const resetModal = () => {
    setTransferSessionSid(null);
    setTransferState('idle');
    setNote('');
    setSelectedRoom(null);
    setRooms([]);
    setRoomsLoadingState('loading');
  }

  /**
   * Effect: Auto-close modal after successful transfer
   * Closes modal and resets state 5 seconds after successful transfer
   */
  useEffect(() => {
    if (transferState === 'success') {
      // If the session is in checkin or testing, close immediately (overlay handles the UI)
      // Otherwise, show success message for 5 seconds
      if (['checkin', 'review', 'testing'].includes(sessionLocation)) {
        // if the location is checkin, suppress the confirmation.
        if (sessionLocation === 'checkin') {
          useAppStore.getState().setDrawerCloseConfirmation(null);
        }
        // Close immediately - the SessionViewer overlay will handle the transfer notification
        resetModal();

        return; // Don't set up the timeout
      }

      // For other locations (queue, etc.), show success message for 5 seconds
      const timer = setTimeout(() => {
        resetModal();
      }, 5000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [transferState, sessionLocation]);

  /**
   * Renders room selection dropdown and notes field
   * 
   * @function renderRoomOptions
   * @returns {React.ReactElement|undefined} Room selection UI or undefined if no rooms
   */
  const renderRoomOptions = () => {
    if (!rooms || rooms.length === 0) {
      return;
    }

    return (
      <>
        <View as="div" margin="small 0">
          <Flex gap="small" alignItems="end">
            <Flex.Item shouldGrow shouldShrink>
              <SimpleSelect
                renderLabel="Available Rooms"
                assistiveText="Use arrow keys to navigate the room options."
                value={selectedRoom || ''}
                onChange={(event, { value }) => setSelectedRoom(value)}
              >
                {rooms.map((room, index) => {
                  return (
                    <SimpleSelect.Option key={index} id={String(index)} value={room.value}>
                      {room.label}
                    </SimpleSelect.Option>
                  );
                })}
              </SimpleSelect>
            </Flex.Item>
            <Flex.Item>
              <IconButton
                onClick={loadRooms}
                renderIcon={IconRefreshLine}
                withBackground={false}
                withBorder={false}
                screenReaderLabel="Refresh available rooms"
              />
            </Flex.Item>
          </Flex>
        </View>
        <View as="div" margin="small 0">
          <TextArea
            label="Transfer Notes (Optional)"
            placeholder="Add any notes about this transfer..."
            value={note}
            onChange={(event) => setNote(event.target.value)}
            resize="vertical"
            height="100px"
          />
        </View>
      </>
    );
  };

  /**
   * Renders no rooms available message with possible causes
   * 
   * @function renderNoRooms
   * @returns {React.ReactElement} No rooms available UI with retry button
   */
  const renderNoRooms = () => {
    return (
      <View as="div">
        <Alert variant="warning">There are no available rooms for transfer.</Alert>
        <Callout
          variant="danger"
          title="Possible Causes"
        >
          <List margin="0 0 medium">
            <List.Item>All online rooms are at capacity.</List.Item>
            <List.Item>There are no rooms online for this location that can service sessions from this student's institution.</List.Item>
            <List.Item>Online rooms are set to not accept any new sessions.</List.Item>
            <List.Item>There are greeter only rooms online.</List.Item>
          </List>
        </Callout>
        <View as="div" textAlign="center">
          <Button variant="primary" onClick={loadRooms}> <IconRefreshLine /> Check Availabile Rooms Again</Button>
        </View>
      </View>
    );
  };

  /**
   * Renders error message when room loading fails
   * 
   * @function renderErrorLoadingRooms
   * @returns {React.ReactElement} Error message UI with retry option
   */
  const renderErrorLoadingRooms = () => {
    return (
      <View as="div">
        <Alert variant="error">There was an error loading the rooms.</Alert>
        <View as="div" textAlign="center" margin="small 0">
          <Button onClick={loadRooms}>
            <IconRefreshLine /> Try Again
          </Button>
        </View>
      </View>
    );
  };

  /**
   * Renders loading spinner while fetching rooms
   * 
   * @function renderLoadingRooms
   * @returns {React.ReactElement} Loading indicator UI
   */
  const renderLoadingRooms = () => {
    return (
      <Flex alignItems="center" gap="small">
        <Spinner size="x-small" renderTitle="Loading available rooms" />
        <Text>Loading available rooms</Text>
      </Flex>
    );
  };

  /**
   * Extracts room name from label (text before pipe character)
   * 
   * Room labels may contain additional info after a pipe (e.g., "Room A | 5 students").
   * This function extracts only the room name portion.
   * 
   * @function getRoomName
   * @param {string} label - Full room label
   * @returns {string} Room name without additional info
   * 
   * @example
   * getRoomName("Room A | 5 students") // Returns "Room A"
   * getRoomName("Room B") // Returns "Room B"
   * getRoomName("") // Returns ""
   */
  const getRoomName = (label) => {
    if (!label) return '';
    const parts = label.split('|');
    return parts[0].trim();
  };

  /**
   * Renders success message after transfer completes
   * 
   * Shows success icon, confirmation message, and auto-close countdown.
   * 
   * @function renderTransferSuccess
   * @returns {React.ReactElement} Success message UI
   */
  const renderTransferSuccess = () => {
    const selectedRoomLabel = rooms.find(room => room.value === selectedRoom)?.label;
    const roomName = getRoomName(selectedRoomLabel);

    return (
      <View as="div" textAlign="center">
        <Text color="success">
          <Icon name="IconCircleCheckFilled" size="large" />
        </Text>
        <Text as="h2" variant="heading">
          Transfer successful!
        </Text>
        <Text as="p" size="small" color="secondary">
          <b>{session?.firstName} {session?.lastName}</b> has been transferred to <b>{roomName}</b>.
        </Text>
        <View as="div" margin="small 0" textAlign="center">
          <Text size="small" color="secondary">
            This window will close automatically in 5 seconds...
          </Text>
        </View>
      </View>
    );
  };

  /**
   * Renders transferring state with spinner
   * 
   * @function renderTransferring
   * @returns {React.ReactElement} Transferring indicator UI
   */
  const renderTransferring = () => {
    return (
      <Flex alignItems="center" gap="small" justifyItems="center">
        <Spinner size="small" renderTitle="Transferring session..." />
        <Text>Transferring session...</Text>
      </Flex>
    );
  };

  /**
   * Handles transfer execution
   * 
   * Validates selection, executes transfer, and updates state based on result.
   * 
   * @async
   * @function handleTransfer
   * @returns {Promise<void>}
   */
  const handleTransfer = async () => {
    // Validate room selection
    if (!selectedRoom) {
      console.error('[TransferModal] No room selected');
      return;
    }

    if (!transferSessionSid) {
      console.error('[TransferModal] No transfer session SID');
      return;
    }

    setTransferState('transferring');

    try {
      await transfer(selectedRoom, note || null);
      setTransferState('success');
      // if the session is not in waiting, reset the modal right away..
      if (session?.location !== 'waiting' && focusedSessionSid === transferSessionSid) {
        resetModal();
      }
    } catch (error) {
      console.error('[TransferModal] Transfer failed:', error);
      setTransferState('error');
      // TODO: Show error message to user
    }
  };

  // Don't render if no session to transfer
  if (!transferSessionSid) {
    return null;
  }

  return (
    <Modal
      open={Boolean(transferSessionSid)}
      onDismiss={() => {
        resetModal();
      }}
      onClose={() => {
        resetModal();
      }}
      label="Transfer Session"
      size="small"
      shouldReturnFocus={false}
      shouldCloseOnDocumentClick={true}
    >
      <Modal.Header spacing="compact">
        <CloseButton
          placement="end"
          offset="small"
          onClick={() => resetModal()}
          screenReaderLabel="Close"
        />
        <Heading level="h3">Transfer Session</Heading>
      </Modal.Header>
      <Modal.Body>
        <View margin="medium none none none">
          {transferState === 'transferring' && renderTransferring()}
          {transferState === 'success' && renderTransferSuccess()}
          {transferState === 'idle' && (
            <>
              {roomsLoadingState === 'loading' && renderLoadingRooms()}
              {roomsLoadingState === 'loaded' && renderRoomOptions()}
              {roomsLoadingState === 'error' && renderErrorLoadingRooms()}
              {roomsLoadingState === 'loaded' &&
                rooms.length === 0 &&
                renderNoRooms()}
            </>
          )}
        </View>
      </Modal.Body>
      {(roomsLoadingState === 'loaded' && rooms.length > 0 && transferState === 'idle') && (
        <Modal.Footer>
          <Button
            onClick={handleTransfer}
            color="primary"
            interaction={selectedRoom ? 'enabled' : 'disabled'}
          >
            Confirm Transfer
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default TransferModal;
